import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, PackageSearch, Loader2 } from "lucide-react";
import api from "../lib/api";
import { TrackingTimeline } from "../components/TrackingTimeline";

export default function Track() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true); setError(""); setData(null);
    try {
      const res = await api.get(`/track/${encodeURIComponent(q)}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "We couldn't find a shipment with that number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16" data-testid="track-page">
      <div className="text-center mb-10">
        <PackageSearch className="w-10 h-10 text-gold mx-auto" />
        <h1 className="font-serif text-4xl sm:text-5xl text-gold-light mt-4">Track your order</h1>
        <p className="text-muted-foreground mt-2 text-sm">Enter your AWB tracking number or order number to see live status.</p>
      </div>

      <form onSubmit={track} className="flex gap-2 mb-10" data-testid="track-form">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. 90657836102 or #A1B2C3D4"
          data-testid="track-input"
          className="flex-1 bg-obsidian-2 border border-gold/25 rounded-full px-5 py-3 text-sm text-gold-muted focus:outline-none focus:border-gold"
        />
        <button type="submit" disabled={loading} data-testid="track-submit"
          className="bg-gold text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Track
        </button>
      </form>

      {error && (
        <div className="glass rounded-lg p-6 text-center text-muted-foreground" data-testid="track-error">{error}</div>
      )}

      {data && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-xl p-6 sm:p-8" data-testid="track-result">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-muted-foreground">Order</p>
              <p className="font-mono text-gold">#{(data.order_id || "").slice(4, 12).toUpperCase()}</p>
            </div>
            <span className="text-xs px-3 py-1 rounded-full bg-gold/15 text-gold capitalize" data-testid="track-status">
              {(data.status || "pending").replace("_", " ")}
            </span>
          </div>
          <TrackingTimeline data={data} />
        </motion.div>
      )}
    </div>
  );
}
